import React, { useRef, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, Check, Sparkles, Clock, Smartphone, Utensils, LayoutGrid, Cpu, TrendingUp, Zap } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProductPageLayout from '../components/ProductPageLayout';

const HERO_VIDEO = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/1x9erssp_ALTERNATIVE.mp4';
const EPOS_PRODUCT_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/htmfss8j_EPOS%20%26%20MINI%203.png';

/* ─────────────────────────────────────── HERO ─────────────────────────────────────── */
const EposHero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = ['/', 'footer'].includes(location.state?.from);
  const fromProductNav = location.state?.from === 'product-pagination';
  const productNavReturnTo = location.state?.returnTo;
  const fromTopNav = location.state?.from === 'top-nav';
  const videoRef = useRef(null);

  // Make sure autoplay kicks in reliably
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.playsInline = true;
      const attempt = v.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }
  }, []);

  return (
    <section className="relative overflow-hidden bg-black" data-testid="epos-hero">
      {/* Background video */}
      <video
        ref={videoRef}
        src={HERO_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="epos-hero-video"
      />

      {/* Left-heavy dark scrim — dark on left so text is legible, fades right so video reads */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to right, rgba(8,14,24,0.88) 0%, rgba(8,14,24,0.72) 35%, rgba(8,14,24,0.35) 60%, rgba(8,14,24,0.1) 85%, rgba(8,14,24,0) 100%)',
        }}
      />
      {/* Subtle bottom fade to blend into next section */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#0a1220]/60 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-28 sm:pb-24 lg:pt-28 lg:pb-24 min-h-[75vh] flex flex-col">
        <button
          onClick={() => {
            if (fromTopNav) navigate('/');
            else if (fromProductNav && productNavReturnTo) navigate(productNavReturnTo);
            else if (fromHome) navigate(-1);
            else navigate('/products');
          }}
          className="self-start flex items-center gap-2 text-white/85 hover:text-white font-medium mb-14 sm:mb-16 transition-colors duration-200"
          data-testid="product-back-button"
        >
          <ArrowLeft size={18} />
          {fromTopNav ? 'Back to Home' : (fromHome || fromProductNav) ? 'Back' : 'Back to Products'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center flex-1">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/50 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.18em] mb-6 backdrop-blur-sm"
              data-testid="epos-eyebrow"
            >
              <Utensils size={12} />
              Hospitality
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-[88px] font-bold text-white leading-[1.05] tracking-tight mb-6"
              style={{ textShadow: '0 6px 28px rgba(0,0,0,0.55)' }}
              data-testid="product-title"
            >
              EPOS &<br />EPOS Mini
            </h1>

            <p
              className="text-lg sm:text-xl lg:text-2xl text-white/95 leading-relaxed max-w-2xl mb-10 font-medium"
              style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
            >
              All-in-one ordering and payment systems<br />
              designed for hospitality excellence.
            </p>

            <button
              type="button"
              onClick={() => { window.__resetSourceScroll?.(); navigate('/get-started-epos'); }}
              className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-xl hover:shadow-2xl"
              data-testid="epos-cta-button"
            >
              Get Started Here</button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────── PRODUCT MOMENT ─────────────────────────────────────── */
/* Image sits naked on the page body with a soft radial mask fading its own dark edges.
   No box, no card, no wrapper — per user instruction. */
const ProductMoment = () => (
  <section className="relative pt-16 sm:pt-20 lg:pt-24" data-testid="epos-product-moment">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.2em] mb-4">
        The EPOS Family
      </p>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight mb-5">
        Cutting Edge Software<br />Sleek Modern Hardware
      </h2>
      <p className="text-lg text-gray-900 font-bold leading-relaxed max-w-3xl mx-auto mb-4">
        Revolutionise your hospitality operations with eCOMM's next-generation EPOS systems. Cloud-based Point of Sale solutions that give merchants seamless, scalable tools for managing day-to-day operations.
      </p>
    </div>

    {/* Image — no wrapper, no card, no background. Soft mask fades the image's baked-in dark edges into the page. */}
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 -mt-4 sm:-mt-8">
      <img
        src={EPOS_PRODUCT_IMAGE}
        alt="eCOMM EPOS and EPOS Mini — full-service monitor with handheld terminal"
        className="w-full h-auto mx-auto block"
        style={{
          maskImage:
            'radial-gradient(ellipse 85% 78% at 50% 50%, #000 55%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 85% 78% at 50% 50%, #000 55%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
        }}
        loading="eager"
        fetchpriority="high"
        decoding="async"
        data-testid="epos-product-image"
      />
    </div>
  </section>
);

/* ─────────────────────────────────────── SHARED UI ─────────────────────────────────────── */
const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <CheckCircle2 size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
    <span className="text-gray-700 text-sm sm:text-base leading-relaxed">{children}</span>
  </li>
);

const SectionHeading = ({ eyebrow, title, subtitle, testId }) => (
  <div className="mb-6" data-testid={testId}>
    {eyebrow && (
      <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.2em] mb-2">{eyebrow}</p>
    )}
    <h2 className="text-3xl sm:text-[2.25rem] font-bold text-gray-900 leading-tight tracking-tight mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">{subtitle}</p>}
    <div className="h-1 w-20 bg-[#1F4E79] rounded-full mt-4" />
  </div>
);

const FeatureChip = ({ icon: Icon, children, testId }) => (
  <div
    className="flex items-start gap-3 rounded-xl bg-white border border-gray-200 hover:border-[#1F4E79]/40 hover:shadow-sm p-4 transition-all duration-200"
    data-testid={testId}
  >
    {Icon && (
      <div className="flex-shrink-0 mt-0.5 text-[#1F4E79]">
        <Icon size={20} strokeWidth={1.75} />
      </div>
    )}
    <span className="text-[14px] text-gray-800 font-medium leading-snug">{children}</span>
  </div>
);

const PerfectFor = ({ tone = 'amber', children, testId }) => {
  const tones = {
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    sky: 'border-sky-200 bg-sky-50 text-sky-900',
  };
  return (
    <div className={`mt-6 rounded-md border p-4 text-sm ${tones[tone]}`} data-testid={testId}>
      <strong>Perfect For:</strong> {children}
    </div>
  );
};

/* ─────────────────────────────────────── PAGE ─────────────────────────────────────── */
const ProductsEposAndMini = () => {
  const navigate = useNavigate();
  return (
  <ProductPageLayout
    currentId="epos-and-mini"
    customHero={
      <>
        <EposHero />
        <ProductMoment />
      </>
    }
    bodyBgClassName="bg-[#f7f6f3]"
    mainTestId="products-epos-page"
  >
    {/* Beyond Traditional Tills */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 mb-10" data-testid="epos-beyond">
      <SectionHeading
        eyebrow="Intelligent Technology"
        title="Beyond Traditional Tills"
        testId="epos-beyond-heading"
      />
      <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
        Unlike conventional systems, our EPOS solutions bring intelligent technology to the heart of hospitality — offering realtime sales data, digital menu control and seamless multi device operation. Designed to optimise speed and accuracy whilst delivering a superior guest experience.
      </p>
    </div>

    {/* EPOS — Full Restaurant Management */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-10" data-testid="epos-full">
      <div className="bg-gradient-to-br from-[#0a1929] to-[#163a5f] p-6 sm:p-10">
        <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Full Restaurant Management</p>
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight">EPOS</h3>
        <p className="text-white/85 mt-2 text-base sm:text-lg max-w-2xl">
          Complete restaurant solution for comprehensive operations.
        </p>
      </div>
      <div className="p-6 sm:p-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FeatureChip icon={LayoutGrid} testId="epos-f1">Large touchscreen interface with intuitive menu navigation</FeatureChip>
          <FeatureChip icon={Utensils} testId="epos-f2">Real-time kitchen display integration</FeatureChip>
          <FeatureChip icon={Cpu} testId="epos-f3">Comprehensive inventory and staff management</FeatureChip>
          <FeatureChip icon={TrendingUp} testId="epos-f4">Advanced analytics and reporting capabilities</FeatureChip>
          <FeatureChip icon={Sparkles} testId="epos-f5">Multi-location support for restaurant groups</FeatureChip>
          <FeatureChip icon={Zap} testId="epos-f6">Seamless integration with existing systems</FeatureChip>
        </div>
        <PerfectFor tone="amber" testId="epos-full-perfect">
          Full Service Restaurants, Bars and Large Hospitality Venues Requiring Comprehensive Management Tools.
        </PerfectFor>
      </div>
    </div>

    {/* EPOS Mini */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-10" data-testid="epos-mini">
      <div className="bg-gradient-to-br from-[#1F4E79] to-[#0E88A3] p-6 sm:p-10">
        <p className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em] mb-2">Compact. Powerful. Built for Hospitality.</p>
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight">EPOS Mini</h3>
        <p className="text-white/95 mt-2 text-base sm:text-lg max-w-2xl">
          Purpose built for busy hospitality venues.
        </p>
      </div>
      <div className="p-6 sm:p-10">
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
          Our flagship all in one portable EPOS solution combines order taking, payment processing, receipt printing and connectivity in a single handheld device.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-2">Revolutionary Integration</h4>
        <p className="text-gray-700 leading-relaxed mb-8">
          Where traditional setups require multiple devices such as tablets for ordering, PDQs for payments and separate printers, the EPOS Mini does it all. No wires, no switching between systems, no disruption to service flow.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">Key Benefits</h4>
        <ul className="space-y-3 mb-8">
          <Bullet><strong>Single Device Solution</strong> — combines ordering, checkout and payment processing</Bullet>
          <Bullet><strong>No Hardware Clutter</strong> — reduces costs and eliminates multiple device management</Bullet>
          <Bullet><strong>Improved Service Speed</strong> — significantly enhances staff efficiency</Bullet>
          <Bullet><strong>Table Service Ready</strong> — portable device supports table service and takeaway</Bullet>
          <Bullet><strong>Realtime</strong> — instant order transmission to kitchen displays</Bullet>
        </ul>

        <PerfectFor tone="sky" testId="epos-mini-perfect">
          Restaurants, Cafés, Quick-Service Venues and Retail Establishments Seeking Streamlined Operations.
        </PerfectFor>

        <h4 className="text-xl font-bold text-gray-900 mt-10 mb-3">Accessibility for Every Business</h4>
        <p className="text-gray-700 leading-relaxed mb-6">
          The EPOS Mini gives smaller businesses access to technology traditionally reserved for large chain restaurants providing sophisticated point-of-sale capabilities at an approachable price point.
        </p>

        <h4 className="text-xl font-bold text-gray-900 mb-4">What You Get</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { icon: Smartphone, text: 'Complete ordering, checkout and payment on one device' },
            { icon: Utensils,   text: 'Tailored for restaurants, cafés, quick-service and retail' },
            { icon: TrendingUp, text: 'Real-time sales insights and simplified reconciliation' },
            { icon: Zap,        text: 'Portable design supporting both table service and takeaway operations' },
          ].map((item, i) => {
            const ItemIcon = item.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl bg-white border border-gray-200 hover:border-[#1F4E79]/40 hover:shadow-sm p-4 transition-all duration-200"
                data-testid={`epos-mini-getyou-${i}`}
              >
                <div className="flex-shrink-0 mt-0.5 text-[#1F4E79]">
                  <ItemIcon size={20} strokeWidth={1.75} />
                </div>
                <span className="text-[14px] text-gray-800 font-medium leading-snug">{item.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* Final CTA strip */}
    <div
      className="rounded-xl bg-gradient-to-br from-[#0a1929] to-[#163a5f] p-8 sm:p-12 text-center shadow-lg mb-2"
      data-testid="epos-final-cta"
    >
      <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight mb-4">
        Transform your hospitality operations.
      </h3>
      <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
        Let's build the right EPOS setup for your venue.
      </p>
      <button
        type="button"
        onClick={() => { window.__resetSourceScroll?.(); navigate('/get-started-epos'); }}
        className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
        data-testid="epos-final-cta-button"
      >
        Get Started Here</button>
    </div>
  </ProductPageLayout>
  );
};

export default ProductsEposAndMini;
